import { SearchableSelect } from "@/components/ui/searchable-select";
import { useState, useEffect } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Sliders,
  HelpCircle,
  Compass,
  UserX,
  TrendingDown,
  Building2,
  Boxes,
  Scale,
} from "lucide-react";
import { PageHeader } from "@/components/patterns";
import type { MasterLookupRecord } from "@/types";

export const MASTER_CATEGORIES = [
  { key: "LEAD_SOURCE", label: "Lead Sources", icon: Compass, description: "Origin of inbound & outbound leads" },
  { key: "LEAD_DISQUALIFY_REASON", label: "Disqualify Reasons", icon: UserX, description: "Why leads are dropped" },
  { key: "DEAL_LOST_REASON", label: "Deal Lost Reasons", icon: TrendingDown, description: "Reasons pipeline deals are marked lost" },
  { key: "INDUSTRY", label: "Industries", icon: Building2, description: "Business domains for accounts & leads" },
  { key: "PRODUCT_CATEGORY", label: "Product Categories", icon: Boxes, description: "Classification of goods & services" },
  { key: "PRODUCT_UNIT", label: "Units of Measure", icon: Scale, description: "Units like NOS, LICENSE, MONTH" },
];

export function MasterLookupPage() {
  const [items, setItems] = useState<MasterLookupRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("DEAL_LOST_REASON");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<MasterLookupRecord | null>(null);
  const [deleteItem, setDeleteItem] = useState<MasterLookupRecord | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    category: "LEAD_SOURCE",
    name: "",
    code: "",
    color_code: "#3b82f6",
    description: "",
    sort_order: 0,
    status: true,
  });

  const { success, error } = useToast();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/MasterLookup/List");
      setItems(unwrapArray<MasterLookupRecord>(res));
    } catch (err: any) {
      error("Fetch Failed", err.message || "Failed to load master lookups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openAdd = () => {
    setEditItem(null);
    setForm({
      category: activeCategory,
      name: "",
      code: "",
      color_code: "#3b82f6",
      description: "",
      sort_order: items.filter((i) => i.category === activeCategory).length + 1,
      status: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (item: MasterLookupRecord) => {
    setEditItem(item);
    setForm({
      category: item.category,
      name: item.name,
      code: item.code,
      color_code: item.color_code || "#3b82f6",
      description: item.description || "",
      sort_order: item.sort_order,
      status: item.status,
    });
    setDialogOpen(true);
  };

  const handleNameChange = (val: string) => {
    setForm((prev) => ({
      ...prev,
      name: val,
      code: editItem ? prev.code : val.toUpperCase().replace(/[^A-Z0-9]/g, "_").slice(0, 32),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) {
      error("Validation Error", "Name and code are required");
      return;
    }

    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/api/MasterLookup/Update/${editItem.id}`, form);
        success("Updated", `Master lookup "${form.name}" updated successfully`);
      } else {
        await api.post("/api/MasterLookup/Insert", form);
        success("Created", `Master lookup "${form.name}" created successfully`);
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err: any) {
      error("Save Error", err.message || "Failed to save master lookup");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setSaving(true);
    try {
      await api.delete(`/api/MasterLookup/Delete/${deleteItem.id}`);
      success("Deleted", `"${deleteItem.name}" deleted successfully`);
      setDeleteItem(null);
      fetchItems();
    } catch (err: any) {
      error("Delete Failed", err.message || "Could not delete lookup item");
    } finally {
      setSaving(false);
    }
  };

  const activeCategoryMeta = MASTER_CATEGORIES.find((c) => c.key === activeCategory);

  /** Switch an option on or off without opening the form */
  const setActive = async (item: MasterLookupRecord, status: boolean) => {
    try {
      await api.put(`/api/MasterLookup/Update/${item.id}`, {
        category: item.category,
        name: item.name,
        code: item.code,
        color_code: item.color_code || "#3b82f6",
        description: item.description || "",
        sort_order: item.sort_order,
        status,
      });
      setItems((cur) => cur.map((x) => (x.id === item.id ? { ...x, status } : x)));
    } catch (err: any) {
      error("Could not change", err.message || "Please try again");
    }
  };

  const inList = items.filter((i) => i.category === activeCategory).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name));
  const shown = inList.filter((item) => {
    const q = search.toLowerCase().trim();
    return !q || item.name.toLowerCase().includes(q) || item.code.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <PageHeader title="Master lists" />
      <p className="text-[13px] text-muted-foreground">The choices people pick from across the CRM.</p>

      <div className="flex flex-wrap items-start gap-4">
        <section aria-label="Lists" className="flex-[0_1_240px] rounded-[10px] border border-border bg-card p-2.5">
          <h2 className="px-2 pb-1.5 pt-1 text-sm font-semibold text-foreground">Lists</h2>
          <div className="flex flex-col gap-0.5">
            {MASTER_CATEGORIES.map((cat) => {
              const on = activeCategory === cat.key;
              const count = items.filter((i) => i.category === cat.key).length;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setActiveCategory(cat.key)}
                  aria-current={on ? "true" : undefined}
                  className={`flex h-8 items-center justify-between rounded-md px-2.5 text-[13px] ${on ? "bg-card font-semibold text-foreground shadow-[0_0_0_1px_var(--border)]" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                >
                  <span>{cat.label}</span>
                  <span className={`font-mono text-[11.5px] ${on ? "text-primary" : ""}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="min-w-0 flex-[999_1_560px] space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              <h1 className="text-[15px] font-semibold text-foreground">{activeCategoryMeta?.label}</h1>
              <span className="block text-xs text-muted-foreground">{activeCategoryMeta?.description}</span>
            </span>
            <span className="flex items-center gap-2">
              <Input placeholder="Search this list" value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 w-52 text-xs" />
              <Button size="sm" onClick={openAdd}>
                + Add option
              </Button>
            </span>
          </div>

          <div className="overflow-x-auto rounded-[10px] border border-border bg-card">
            {loading ? (
              <div className="p-8 text-center text-xs text-muted-foreground">Loading…</div>
            ) : shown.length === 0 ? (
              <div className="space-y-1.5 p-10 text-center">
                <HelpCircle className="mx-auto size-8 text-muted-foreground/50" />
                <p className="text-sm font-semibold text-foreground">{search ? "Nothing matches" : "This list is empty"}</p>
                <p className="text-xs text-muted-foreground">{search ? "Try a different word." : "Add the first option."}</p>
              </div>
            ) : (
              <table className="w-full min-w-[620px] border-collapse text-[12.5px]">
                <thead>
                  <tr className="bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="h-[34px] w-10 border-b border-border px-3 text-center font-semibold">#</th>
                    <th className="border-b border-border px-2.5 font-semibold">Option</th>
                    <th className="w-[170px] border-b border-border px-2.5 font-semibold">Code</th>
                    <th className="w-[90px] border-b border-border px-2.5 font-semibold">Type</th>
                    <th className="w-[70px] border-b border-border px-2.5 text-center font-semibold">Active</th>
                    <th className="w-[84px] border-b border-border px-2.5">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((item, idx) => (
                    <tr key={item.id} className={item.status ? "" : "opacity-60"}>
                      <td className="border-b border-border/60 px-3 py-2 text-center font-mono text-muted-foreground">{item.sort_order || idx + 1}</td>
                      <td className="border-b border-border/60 px-2.5 py-2">
                        <span className="flex items-center gap-2">
                          {item.color_code && <span className="size-3 shrink-0 rounded-[4px]" style={{ backgroundColor: item.color_code }} />}
                          <span className="font-medium text-foreground">{item.name}</span>
                        </span>
                        {item.description && <span className="block text-[11.5px] text-muted-foreground">{item.description}</span>}
                      </td>
                      <td className="border-b border-border/60 px-2.5 py-2 font-mono text-[11.5px] text-muted-foreground">{item.code}</td>
                      <td className="border-b border-border/60 px-2.5 py-2">
                        <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-medium ${item.is_system ? "bg-accent text-primary" : "bg-muted text-muted-foreground"}`}>{item.is_system ? "Built in" : "Yours"}</span>
                      </td>
                      <td className="border-b border-border/60 px-2.5 py-2 text-center">
                        <input type="checkbox" role="switch" checked={item.status} onChange={(e) => setActive(item, e.target.checked)} aria-label={`${item.name} active`} className="size-4 accent-primary" />
                      </td>
                      <td className="border-b border-border/60 px-2.5 py-2">
                        <span className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon-sm" onClick={() => openEdit(item)} aria-label={`Edit ${item.name}`}>
                            <Edit2 className="size-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon-sm" onClick={() => setDeleteItem(item)} disabled={item.is_system} aria-label={`Delete ${item.name}`} title={item.is_system ? "Built-in options can be switched off, not deleted" : "Delete"}>
                            <Trash2 className={`size-3.5 ${item.is_system ? "opacity-30" : "text-danger"}`} />
                          </Button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Switch an option off instead of deleting it, so older records keep their meaning. Built-in options can be switched off but not deleted.</p>
        </section>
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl bg-card border-border text-foreground">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
                <Sliders className="size-5 text-primary" />
                <span>{editItem ? "Edit Master Option" : "Add Master Option"}</span>
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
                Configure standardized dropdown choice for CRM modules.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3.5 py-4 text-xs sm:text-sm">
              <div className="space-y-1.5">
                <Label htmlFor="category" className="font-semibold text-xs sm:text-sm">
                  Category *
                </Label>
                <SearchableSelect
                  id="category"
                  value={form.category}
                  disabled={!!editItem}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                  className="w-full h-9 sm:h-10 rounded-md border border-input bg-background px-3 text-xs sm:text-sm focus:ring-1 focus:ring-ring"
                >
                  {MASTER_CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label} ({c.key})
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="name" className="font-semibold text-xs sm:text-sm">
                  Display Label / Name *
                </Label>
                <Input
                  id="name"
                  placeholder="e.g. Inbound Website, Price Too High"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="code" className="font-semibold text-xs sm:text-sm">
                  Code / Identifier *
                </Label>
                <Input
                  id="code"
                  placeholder="e.g. WEBSITE_INBOUND"
                  value={form.code}
                  disabled={editItem?.is_system}
                  onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                  required
                  className="font-mono"
                />
                <span className="text-[10px] sm:text-xs text-muted-foreground">
                  A short unique code for this value, used in reports
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="color_code" className="font-semibold text-xs sm:text-sm">
                    Color Badge (Optional)
                  </Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      id="color_code"
                      value={form.color_code || "#3b82f6"}
                      onChange={(e) => setForm((p) => ({ ...p, color_code: e.target.value }))}
                      className="size-9 rounded border cursor-pointer p-0.5 bg-background shrink-0"
                    />
                    <Input
                      value={form.color_code || ""}
                      placeholder="#3b82f6"
                      onChange={(e) => setForm((p) => ({ ...p, color_code: e.target.value }))}
                      className="font-mono flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="sort_order" className="font-semibold text-xs sm:text-sm">
                    Sort Order
                  </Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value) }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="description" className="font-semibold text-xs sm:text-sm">
                  Description / Notes
                </Label>
                <Input
                  id="description"
                  placeholder="Additional context about this option"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="status"
                  checked={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.checked }))}
                  className="rounded border-input text-primary focus:ring-primary size-4"
                />
                <Label htmlFor="status" className="font-medium text-xs sm:text-sm cursor-pointer">
                  Active (visible in dropdowns across CRM)
                </Label>
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : editItem ? "Update Option" : "Create Option"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteItem} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg text-danger">Confirm Delete</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Are you sure you want to delete <strong className="text-foreground">{deleteItem?.name}</strong>? Existing historical records
              will keep their string code, but this option will no longer appear in dropdowns.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setDeleteItem(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving ? "Deleting..." : "Delete Option"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
